import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { format, isAfter, isBefore, isEqual, subDays } from 'date-fns';
import { Search, WifiOff, Wifi, Lightbulb, Calendar, Power, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DateRange } from 'react-day-picker';

// Enum for event types
enum EventType {
  DEVICE_CONNECTED = 'DEVICE_CONNECTED',
  DEVICE_DISCONNECTED = 'DEVICE_DISCONNECTED',
  FORCE_CHANGED = 'FORCE_CHANGED',
  SCHEDULE_UPDATED = 'SCHEDULE_UPDATED',
  RELAY_TOGGLED = 'RELAY_TOGGLED'
}

// Mock data for events (replace with actual API calls)
interface Event {
  id: string;
  device_id: string;
  message: EventType;
  datetime: Date;
  read: boolean;
}

// Simple DateRangePicker directly in this file to avoid dependencies
function SimpleDateRangePicker({
  date,
  onDateChange,
  className,
}: {
  date: DateRange;
  onDateChange: (date: DateRange) => void;
  className?: string;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const placeholder = t('events.dateRange', 'Select date range');
  
  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "dd/MM/yyyy")} -{" "}
                  {format(date.to, "dd/MM/yyyy")}
                </>
              ) : (
                format(date.from, "dd/MM/yyyy")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(selectedDate) => {
              if (selectedDate) {
                onDateChange(selectedDate);
              }
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Helper function to get icon based on event type
const getEventIcon = (eventType: EventType) => {
  switch(eventType) {
    case EventType.DEVICE_CONNECTED:
      return <Wifi className="h-5 w-5 text-green-500" />;
    case EventType.DEVICE_DISCONNECTED:
      return <WifiOff className="h-5 w-5 text-red-500" />;
    case EventType.FORCE_CHANGED:
      return <Lightbulb className="h-5 w-5 text-yellow-500" />;
    case EventType.SCHEDULE_UPDATED:
      return <Calendar className="h-5 w-5 text-blue-500" />;
    case EventType.RELAY_TOGGLED:
      return <Power className="h-5 w-5 text-purple-500" />;
    default:
      return null;
  }
};

// Helper function to get translated message based on event type
const getEventMessage = (eventType: EventType, t: TFunction) => {
  switch(eventType) {
    case EventType.DEVICE_CONNECTED:
      return t('events.deviceConnected', 'Device Connected');
    case EventType.DEVICE_DISCONNECTED:
      return t('events.deviceDisconnected', 'Device Disconnected');
    case EventType.FORCE_CHANGED:
      return t('events.forceChanged', 'Force State Changed');
    case EventType.SCHEDULE_UPDATED:
      return t('events.scheduleUpdated', 'Schedule Updated');
    case EventType.RELAY_TOGGLED:
      return t('events.relayToggled', 'Relay Toggled');
    default:
      return '';
  }
};

const EventsPage = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [eventType, setEventType] = useState('all');

  // Generate static mock data with specific device ID pattern
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const eventTypes = [
        EventType.DEVICE_CONNECTED,
        EventType.DEVICE_DISCONNECTED,
        EventType.FORCE_CHANGED,
        EventType.SCHEDULE_UPDATED,
        EventType.RELAY_TOGGLED
      ];
      
      // Create a consistent pseudo-random generator for deterministic results
      const pseudoRandom = (seed: number): number => {
        return ((seed * 9301 + 49297) % 233280) / 233280;
      };
      
      const mockEvents: Event[] = [];
      
      // Generate events for device IDs from g-2025-001 to g-2025-050
      for (let i = 1; i <= 50; i++) {
        // Format the device number with leading zeros
        const deviceNumber = i.toString().padStart(3, '0');
        const deviceId = `g-2025-${deviceNumber}`;
        
        // Generate 1-3 events for each device
        const eventCount = Math.floor(pseudoRandom(i) * 3) + 1;
        
        for (let j = 0; j < eventCount; j++) {
          const eventSeed = i * 100 + j;
          const eventType = eventTypes[Math.floor(pseudoRandom(eventSeed * 3) * eventTypes.length)];
          
          // Create timestamp within last 30 days, more recent for lower device numbers
          const daysAgo = Math.floor(pseudoRandom(eventSeed) * 30);
          const hoursAgo = Math.floor(pseudoRandom(eventSeed * 2) * 24);
          const minutesAgo = Math.floor(pseudoRandom(eventSeed * 4) * 60);
          
          const date = new Date();
          date.setDate(date.getDate() - daysAgo);
          date.setHours(date.getHours() - hoursAgo);
          date.setMinutes(date.getMinutes() - minutesAgo);
          
          mockEvents.push({
            id: `event-${deviceId}-${j}`,
            device_id: deviceId,
            message: eventType,
            datetime: date,
            read: pseudoRandom(eventSeed * 5) > 0.3 // 70% chance of being read
          });
        }
      }
      
      // Sort events by datetime in descending order (most recent first)
      const sortedEvents = mockEvents.sort((a, b) => b.datetime.getTime() - a.datetime.getTime());
      
      setEvents(sortedEvents);
      setFilteredEvents(sortedEvents);
      setLoading(false);
    }, 1000);
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...events];
    
    // Filter by search term (device_id)
    if (searchTerm) {
      result = result.filter(
        event => event.device_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by date range - safely check for dates
    if (dateRange.from && dateRange.to) {
      result = result.filter(
        event => {
          // Check if event date is within range
          return (
            (isAfter(event.datetime, dateRange.from as Date) || isEqual(event.datetime, dateRange.from as Date)) &&
            (isBefore(event.datetime, dateRange.to as Date) || isEqual(event.datetime, dateRange.to as Date))
          );
        }
      );
    }
    
    // Filter by event type
    if (eventType !== 'all') {
      const evType = eventType.toUpperCase();
      result = result.filter(event => {
        switch(evType) {
          case 'CONNECTED':
            return event.message === EventType.DEVICE_CONNECTED;
          case 'DISCONNECTED':
            return event.message === EventType.DEVICE_DISCONNECTED;
          case 'FORCE':
            return event.message === EventType.FORCE_CHANGED;
          case 'SCHEDULE':
            return event.message === EventType.SCHEDULE_UPDATED;
          case 'RELAY':
            return event.message === EventType.RELAY_TOGGLED;
          default:
            return true;
        }
      });
    }
    
    setFilteredEvents(result);
  }, [searchTerm, dateRange, eventType, events]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle event type filter change
  const handleEventTypeChange = (value: string) => {
    setEventType(value);
  };

  // Format date to DD/MM/YYYY HH:mm
  const formatDate = (date: Date) => {
    return format(date, 'dd/MM/yyyy HH:mm');
  };

  // Mark an event as read
  const markAsRead = (eventId: string) => {
    const updatedEvents = events.map(event => 
      event.id === eventId ? { ...event, read: true } : event
    );
    setEvents(updatedEvents);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('events.title', 'Events')}</CardTitle>
          <CardDescription>
            {t('events.description', 'View and filter system events')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col gap-4 md:flex-row mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder={t('events.searchEvents', 'Search device ID...')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-8"
              />
            </div>
            
            <Select onValueChange={handleEventTypeChange} defaultValue="all">
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder={t('events.filterByType', 'Filter by type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('events.allEvents', 'All Events')}</SelectItem>
                <SelectItem value="connected">{t('events.deviceConnected', 'Device Connected')}</SelectItem>
                <SelectItem value="disconnected">{t('events.deviceDisconnected', 'Device Disconnected')}</SelectItem>
                <SelectItem value="force">{t('events.forceChanged', 'Force State Changed')}</SelectItem>
                <SelectItem value="schedule">{t('events.scheduleUpdated', 'Schedule Updated')}</SelectItem>
                <SelectItem value="relay">{t('events.relayToggled', 'Relay Toggled')}</SelectItem>
              </SelectContent>
            </Select>
            
            <SimpleDateRangePicker
              date={dateRange}
              onDateChange={setDateRange}
              className="w-full md:w-auto"
            />
          </div>
          
          {/* Events Table */}
          {loading ? (
            <div className="py-8 text-center">{t('common.loading', 'Loading...')}</div>
          ) : filteredEvents.length === 0 ? (
            <div className="py-8 text-center">{t('events.noEventsFound', 'No events found')}</div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead>{t('events.deviceId', 'Device ID')}</TableHead>
                    <TableHead>{t('events.message', 'Message')}</TableHead>
                    <TableHead>{t('events.datetime', 'Date & Time')}</TableHead>
                    <TableHead className="w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEvents.map((event) => (
                    <TableRow key={event.id} className={!event.read ? "font-semibold bg-gray-50" : ""}>
                      <TableCell className="p-3">
                        {getEventIcon(event.message)}
                      </TableCell>
                      <TableCell>{event.device_id}</TableCell>
                      <TableCell>{getEventMessage(event.message, t)}</TableCell>
                      <TableCell>{formatDate(event.datetime)}</TableCell>
                      <TableCell>
                        {!event.read && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => markAsRead(event.id)}
                            title={t('events.markAsRead', 'Mark as read')}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination placeholder - would be implemented based on your pagination system */}
          <div className="flex justify-between items-center mt-4">
            <div>
              {t('events.showing', 'Showing')} {filteredEvents.length} {t('events.of', 'of')} {events.length} {t('events.events', 'events')}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                {t('common.previous', 'Previous')}
              </Button>
              <Button variant="outline" size="sm" disabled>
                {t('common.next', 'Next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EventsPage; 